import { createServerFn } from "@tanstack/react-start";
import {
  CallscreeningResponse,
  CreateQueueAttendant,
  QueueGroupReadable,
  QueueGroupWritable,
  QueueWritable,
  SettingListResponse,
  UpdateQueueGroupAttendantsResponse,
} from "@/lib/mma/types.gen";
import { collectErrorMessages, createAuthenticatedApiConfig } from "@/lib/utils.server";
import {
  createQueue,
  createQueueGroup,
  deleteAllQueueSettings,
  deleteQueue,
  deleteQueueGroup,
  deleteQueueSetting,
  getQueue,
  getQueueAttendants,
  getQueueCallscreening,
  getQueueGroup,
  getQueueGroupQueues,
  getQueueGroups,
  getQueues,
  getQueueSettings,
  testQueueCallscreening,
  updateQueue,
  updateQueueAttendants,
  updateQueueCallscreening,
  updateQueueGroup,
  updateQueueGroupQueues,
  updateQueueSettings,
  getQueueGroupAttendants,
  updateQueueGroupAttendants,
} from "@/lib/mma";

export const getQueuesFn = createServerFn({ method: "GET" }).handler(async (): Promise<any[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getQueues(config);
  const queues = response?.data?.data || [];

  console.log("<====== getQueues response", queues);
  return queues.map((queue) => ({
    id: queue.id!,
    name: queue.name!,
    href: `/queues/${queue.id}`,
    dropdown: [
      { id: `${queue.id}-edit`, name: "Edit", href: `/queues/${queue.id}/edit` },
      { id: `${queue.id}-settings`, name: "Settings", href: `/queues/${queue.id}/settings` },
      { id: `${queue.id}-agents`, name: "Agents", href: `/queues/${queue.id}/agents` },
      { id: `${queue.id}-call-screening`, name: "Call Screening", href: `/queues/${queue.id}/call-screening` },
      {
        id: `${queue.id}-delete`,
        name: "Delete",
        onClick: "delete",
        color: "red-500",
        parent: { name: queue.name, id: queue.id },
      },
    ],
  }));
});

//There are settings that rely on the query key being exactly "queues", so don't change it unless it is changed in settings. See settings-fields.tsx for more details.
export const queuesQueryOptions = {
  queryKey: ["queues"],
  queryFn: getQueuesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const createQueueFn = createServerFn({ method: "POST" })
  .inputValidator((data: QueueWritable) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("<====== createQueueFn data", data);
    const response = await createQueue({
      ...config,
      body: data,
    });
    console.log("<====== createQueueFn response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create queue");
    }
    return response.data || {};
  });

export const fetchQueue = createServerFn({ method: "GET" })
  .inputValidator((d: { queueId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueue({
      ...config,
      path: {
        queueId: data.queueId,
      },
    });
    console.log("<====== fetchQueue response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get queue");
    }

    return response.data?.data || {};
  });

export const queueQueryOptions = (queueId: number) => ({
  queryKey: ["queue", queueId],
  queryFn: () => fetchQueue({ data: { queueId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

export const updateQueueFn = createServerFn()
  .inputValidator((data: { queueId: number; body: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("<====== updateQueueFn data", data);
    const response = await updateQueue({
      ...config,
      path: { queueId: data.queueId },
      body: data.body,
    });
    console.log("=======> updateQueueFn response", response);
    return response.data;
  });

export const deleteQueueFn = createServerFn()
  .inputValidator((data: { queueId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteQueue({
      ...config,
      path: { queueId: data.queueId },
    });
    return response.data;
  });

export const fetchQueueSettings = createServerFn({ method: "GET" })
  .inputValidator((data: { queueId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueSettings({
      ...config,
      path: {
        id: data.queueId,
      },
    });
    return (response.data?.data as SettingListResponse) || {};
  });

export const queueSettingsQueryOptions = (queueId: number) => ({
  queryKey: ["queue-settings", queueId],
  queryFn: () => fetchQueueSettings({ data: { queueId } }),
});

export const updateQueueSettingsFn = createServerFn()
  .inputValidator((data: { queueId: number; body: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("<====== updateQueueSettingsFn data", data);
    const response = await updateQueueSettings({
      ...config,
      path: { id: data.queueId },
      body: data.body,
    });
    console.log("<====== updateQueueSettingsFn response", response);
    return response.data;
  });

// Queue Groups APIs
export const getQueueGroupsFn = createServerFn({ method: "GET" }).handler(async (): Promise<any[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getQueueGroups(config);
  if (!response.data) {
    throw new Error(collectErrorMessages(response.error) || "Failed to get queue groups");
  }
  return response.data.data?.map((queueGroup) => ({
    id: queueGroup.id!,
    name: queueGroup.name!,
    href: `/queue-groups/${queueGroup.id}`,
    dropdown: [
      { id: `${queueGroup.id}-edit`, name: "Edit", href: `/queue-groups/${queueGroup.id}/edit` },
      { id: `${queueGroup.id}-queues`, name: "Queues", href: `/queue-groups/${queueGroup.id}/queues` },
      { id: `${queueGroup.id}-agents`, name: "Agents", href: `/queue-groups/${queueGroup.id}/agents` },
      {
        id: `${queueGroup.id}-delete`,
        name: "Delete",
        onClick: "delete",
        color: "red-500",
        parent: { name: queueGroup.name, id: queueGroup.id },
      },
    ],
  }));
});

export const queueGroupsQueryOptions = {
  queryKey: ["queue-groups"],
  queryFn: getQueueGroupsFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const fetchQueueGroup = createServerFn({ method: "GET" })
  .inputValidator((data: { queueGroupId: number }) => data)
  .handler(async ({ data }): Promise<QueueGroupReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueGroup({
      ...config,
      path: {
        queueGroupId: data.queueGroupId,
      },
    });
    return response.data?.data as QueueGroupReadable;
  });

export const queueGroupQueryOptions = (queueGroupId: number) => ({
  queryKey: ["queue-group", queueGroupId],
  queryFn: () => fetchQueueGroup({ data: { queueGroupId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

export const queueGroupQueuesQueryOptions = (queueGroupId: number) => ({
  queryKey: ["queue-group-queues", queueGroupId],
  queryFn: () => fetchQueueGroupQueues({ data: { queueGroupId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

export const fetchQueueGroupQueues = createServerFn({ method: "GET" })
  .inputValidator((data: { queueGroupId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueGroupQueues({
      ...config,
      path: { queueGroupId: data.queueGroupId },
    });
    return response.data?.data || [];
  });

export const updateQueueGroupFn = createServerFn()
  .inputValidator((data: { queueGroupId: number; body: any }) => data)
  .handler(async ({ data }): Promise<QueueGroupReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateQueueGroup({
      ...config,
      path: { queueGroupId: data.queueGroupId },
      body: data.body,
    });
    return response.data as QueueGroupReadable;
  });

export const createQueueGroupFn = createServerFn({ method: "POST" })
  .inputValidator((data: { body: QueueGroupWritable }) => data)
  .handler(async ({ data }): Promise<QueueGroupReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createQueueGroup({
      ...config,
      body: data.body,
    });
    return response.data as QueueGroupReadable;
  });

export const deleteQueueGroupFn = createServerFn()
  .inputValidator((data: { queueGroupId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteQueueGroup({
      ...config,
      path: { queueGroupId: data.queueGroupId },
    });
    return response.data;
  });

// Queue Agents APIs
export const getQueueAttendantsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { queueId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueAttendants({
      ...config,
      path: {
        queueId: data.queueId,
      },
    });
    return response.data?.data || [];
  });

export const updateQueueAttendantsFn = createServerFn()
  .inputValidator((data: { queueId: number; body: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateQueueAttendants({
      ...config,
      path: { queueId: data.queueId },
      body: data.body,
    });
    return response.data;
  });

export const queueAttendantsQueryOptions = (queueId: number) => ({
  queryKey: ["queue-attendants", queueId],
  queryFn: () => getQueueAttendantsFn({ data: { queueId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

// Queue Settings APIs
export const deleteAllQueueSettingsFn = createServerFn()
  .inputValidator((data: { queueId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteAllQueueSettings({
      ...config,
      path: { id: data.queueId },
    });
    return response.data;
  });

export const deleteQueueSettingFn = createServerFn()
  .inputValidator((data: { queueId: number; settingName: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteQueueSetting({
      ...config,
      path: {
        id: data.queueId,
        name: data.settingName,
      },
    });
    return response.data;
  });

// Queue Callscreening APIs
export const getQueueCallscreeningFn = createServerFn({ method: "GET" })
  .inputValidator((d: { queueId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueCallscreening({
      ...config,
      path: {
        id: data.queueId,
      },
    });
    return response.data?.data || {};
  });

export const queueCallscreeningQueryOptions = (queueId: number) => ({
  queryKey: ["queue-callscreening", queueId],
  queryFn: () => getQueueCallscreeningFn({ data: { queueId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

export const updateQueueCallscreeningFn = createServerFn()
  .inputValidator((data: { queueId: number; body: any }) => data)
  .handler(async ({ data }): Promise<CallscreeningResponse> => {
    const config = await createAuthenticatedApiConfig();
    console.log("<====== updateQueueCallscreeningFn data", data);
    const response = await updateQueueCallscreening({
      ...config,
      path: { id: data.queueId },
      body: data.body,
    });
    console.log("<====== updateQueueCallscreeningFn response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update queue callscreening");
    }
    return response.data || {};
  });

export const testQueueCallscreeningFn = createServerFn({ method: "POST" })
  .inputValidator((data: { queueId: number; payload: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("=======> testQueueCallscreeningFn data: ", data);
    const response = await testQueueCallscreening({
      ...config,
      body: data.payload,
      path: { id: data.queueId },
    });
    console.log("<====== testQueueCallscreeningFn response: ", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to test callscreening");
    }
    return response.data.data;
  });

export const resetQueueSettingsFn = createServerFn()
  .inputValidator((data: { queueId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteAllQueueSettings({
      ...config,
      path: { id: data.queueId },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages((response as any).errors) || "Failed to reset queue settings");
    }
    return;
  });

export const updateQueueGroupQueuesFn = createServerFn()
  .inputValidator((data: { queueGroupId: number; body: Array<number> }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateQueueGroupQueues({
      ...config,
      path: { queueGroupId: data.queueGroupId },
      body: data.body,
    });
    return response.data;
  });

export const getQueueGroupAgentsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { queueGroupId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getQueueGroupAttendants({
      ...config,
      path: { queueGroupId: data.queueGroupId },
    });
    return response.data?.data || [];
  });

export const queueGroupAgentsQueryOptions = (queueGroupId: number) => ({
  queryKey: ["queue-group-agents", queueGroupId],
  queryFn: () => getQueueGroupAgentsFn({ data: { queueGroupId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
});

export const updateQueueGroupAttendantsFn = createServerFn()
  .inputValidator((data: { queueGroupId: number; body: Array<CreateQueueAttendant> }) => data)
  .handler(async ({ data }): Promise<UpdateQueueGroupAttendantsResponse> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateQueueGroupAttendants({
      ...config,
      path: { queueGroupId: data.queueGroupId },
      body: data.body,
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update queue group attendants");
    }
    return response.data || [];
  });

export const clearQueueOverrideFn = createServerFn()
  .inputValidator((data: { id: number; setting: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteQueueSetting({ ...config, path: { id: data.id, name: data.setting } });
    if (!response.data) {
      throw new Error(collectErrorMessages((response as any).error) || "Failed to clear queue override");
    }
    return { success: true };
  });
